import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useRef, useState} from 'react';

import {omit} from 'ramda';

import {useGetSalesWarrantySettingsQuery} from '@dms/api/sales';
import {ConditionEnum, useCreateSaleVehicleV2Mutation, VehicleTypeEnum} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {
  buildBatteryCertificateFileFields,
  SaleVehicle,
  SaleVehicleValidationSchema,
  toAPIVehicle,
  useFormNavigate,
  useGetCarFeatures,
  useIsVehicleQualitySealShown,
  useVehicleWarrantySync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  formatDocumentTitle,
  handleApiError,
  useMandatoryFields,
  FieldAvailabilityContext,
} from '@dms/shared';
import {AuditDataAssetsV2Body} from '@dms/teas';

import {SaleVehicleForm} from '../components/SaleVehicleForm';
import {DEFAULT_VEHICLE_TYPE} from '../constants/vehicleConstants';
import {useAttachPhotosToSaleVehicleAlbum} from '../hooks/useAttachPhotosToSaleVehicleAlbum';
import {useConditionDataSubmit} from '../hooks/useConditionDataSubmit';
import {useCreatedVehicleRedirect} from '../hooks/useCreatedVehicleRedirect';
import {buildRegistrationPayload} from '../utils/buildRegistrationPayload';
import {getTargetAlbum} from '../utils/getTargetAlbum';

export function CreateSaleVehicle() {
  const {navigateOnDiscard, isBusinessCasePurchaseReferrer} = useFormNavigate();
  const {createdVehicleRedirect} = useCreatedVehicleRedirect();

  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const isSalesWarrantyEnabled = isFeatureEnabled(featureFlags.SALES_WARRANTY);
  const isQualitySealShown = useIsVehicleQualitySealShown();

  const {data: warrantySettings, isLoading: isWarrantySettingsLoading} =
    useGetSalesWarrantySettingsQuery(undefined, {skip: !isSalesWarrantyEnabled});

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: DEFAULT_VEHICLE_TYPE,
  });

  const [formSchema, setFormSchema] = useState<ZodSchema<SaleVehicle>>(() =>
    generateSchema(SaleVehicleValidationSchema, currentContextRef.current)
  );

  useEffect(
    function initFormSchema() {
      setFormSchema(generateSchema(SaleVehicleValidationSchema, currentContextRef.current));
    },
    [generateSchema]
  );

  const [getCarFeatures] = useGetCarFeatures();

  const [createSaleVehicleMutation, {isLoading: isCreating}] = useCreateSaleVehicleV2Mutation();

  const {handleSubmit: handleConditionDataSubmit} = useConditionDataSubmit();

  const {attachPhotos} = useAttachPhotosToSaleVehicleAlbum();

  const {syncWarranties, isSyncing} = useVehicleWarrantySync();

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};
      const newSchema = generateSchema(
        SaleVehicleValidationSchema,
        currentContextRef.current,
        condition
      );
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};
      const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
      setFormSchema(newSchema);
    },
    [generateSchema]
  );

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const apiPayload = toAPIVehicle(values, null, sourcingData);

    const onCreate = () =>
      createSaleVehicleMutation({
        createSaleVehicleV2RequestBody: {
          vehicle: apiPayload,
          registration: buildRegistrationPayload(
            values,
            isBusinessCasePurchaseReferrer,
            isQualitySealShown
          ),
          additionalNote: values.additionalNote,
        },
        validateMandatoryFields: isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined),
        fileFields: buildBatteryCertificateFileFields(values.batteryCertificateDocument),
      })
        .unwrap()
        .then((createdVehicle) => createdVehicle.vehicleId)
        .then(async (id) => {
          await attachPhotos(id, values.photos, getTargetAlbum(values.targetAlbum));
          return id;
        })
        .then(async (id) => {
          await handleConditionDataSubmit(
            id,
            omit(['sessionId'], values.auditData ?? {}) ?? {},
            (values.auditAssets as Record<string, AuditDataAssetsV2Body>) ?? {}
          );
          return id;
        })
        .then(async (id) => {
          await syncWarranties({vehicleId: id, warranties: values.warranties ?? []});
          return id;
        })
        .then(createdVehicleRedirect);

    // eslint-disable-next-line eslint-js/no-restricted-syntax
    return onCreate().catch(handleApiError);
  };

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.createVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isMandatoryFieldsLoading || isWarrantySettingsLoading}>
        <VFKit.Form
          showMandatoryFields
          mode="onBlur"
          experimentalZodSchema={formSchema}
          defaultValues={{
            targetAlbum: ['PHOTOS_CAR_PURCHASE'],
            type: [DEFAULT_VEHICLE_TYPE],
            warrantyDuration: warrantySettings?.defaultDuration ?? null,
            warranties: [],
            dimensions: {
              length: null,
              height: null,
              width: null,
            },
            dimensionsLoadingSpace: {
              length: null,
              width: null,
              height: null,
            },
          }}
        >
          <SaleVehicleForm
            onSave={handleSave}
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating || isSyncing}
            shouldShowWarranties
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
